
import {
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { StyledButton } from "../../../../components/Button/Button";
import Modal from "../../../../components/Modal/Modal";
import {
  setPADisclaimerAccepted,
  setAddOns,
  removeAddon,
  setAddOnsWithCover,
  setSelectedAddons,
} from "../../../../modules/quotes.slice";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import useUrlQuery from "../../../../hooks/useUrlQuery";
import useFilters from "../../../../hooks/useFilters";
import { useGetAddonsQuery } from "../../../../services/QuoteServices";

interface PADisclaimerProps {
  onBuyNow: () => void;
  open: boolean;
  onClose: () => void;
}

const PADisclaimer: React.FC<PADisclaimerProps> = ({
  onBuyNow,
  ...props
}) => {
  const dispatch = useDispatch();
  const { selectedAddons } = useTypedSelector((state) => state.quote);
  const { setUrlQuery } = useUrlQuery();
  const { selectedAddonsParam } = useFilters();
  const { addons_list } = useGetAddonsQuery();

  const [selected, setSelected] = useState<null | "PACover" | "check1" | "check2">(null);
  const paCover = addons_list.find((item: { ADD_ON_SLUG: string; }) => item.ADD_ON_SLUG === "PA_COVER");
  useEffect(() => {
    if (selectedAddons.includes("PA_COVER")) {
      setSelected("PACover");
    } else if (selected === "PACover") {
      setSelected(null); // Reset if deselected from outside
    }
  }, [selectedAddons]);

  const handlePACoverChange = (checked: boolean) => {
    let updatedAddOns;

    if (checked) {
      updatedAddOns = [...selectedAddons, "PA_COVER"];
    } else {
      updatedAddOns = selectedAddons.filter((a) => a !== "PA_COVER");
    }

    // ✅ Dispatch setAddOns like Zero Dep check
    dispatch(setAddOns(updatedAddOns));
    dispatch(setPADisclaimerAccepted(checked));
    const { ADD_ON_ID, ADD_ON_SLUG, IS_SI_REQUIRED } = paCover;
    const isSelected = selectedAddons.includes(ADD_ON_SLUG);

    if (isSelected) {
      dispatch(removeAddon(ADD_ON_SLUG));
      if (IS_SI_REQUIRED === "Y") {
        dispatch(setAddOnsWithCover({ key: ADD_ON_SLUG, value: 0 }));
      }
      setUrlQuery("addOns", String(Number(selectedAddonsParam) - ADD_ON_ID));
    } else {
      dispatch(setSelectedAddons(ADD_ON_SLUG));
      setUrlQuery("addOns", String(Number(selectedAddonsParam) + ADD_ON_ID));
    }
  };

  const handleSelect = (
    which: "PACover" | "check1" | "check2",
    checked: boolean
  ) => {
    setSelected((prev) => (checked ? which : null));

    if (which === "PACover") {
      handlePACoverChange(checked);
    } else {
      // For opt-out reasons, don't change selectedAddons but still set disclaimer accepted
      dispatch(setPADisclaimerAccepted(checked));
    }
  };

  const canProceed =
    selected === "PACover" || selected === "check1" || selected === "check2";

  const handleSubmit = () => {
    // If PA_COVER is selected, we already added it inside handlePACoverChange.
    // This ensures it acts same as "Zero Dep" checkbox on submit.

    // Any common side effects if needed can go here.

    onBuyNow(); // Trigger purchase logic
  };

  return (
    <Modal
      title="Personal Accident Cover is Mandatory as per IRDAI"
      open={props.open}
      onClose={props.onClose}
      maxWidth={"xs"}
    >
      <Stack p={2} spacing={1}>
        <FormControlLabel
          label="Personal Accident Cover"
          control={
            <Checkbox
              checked={selected === "PACover"}
              onChange={(e) => handleSelect("PACover", e.target.checked)}
            />
          }
        />

        <Typography variant="body2">
          You can only opt out if,
        </Typography>

        <FormControlLabel
          label="Owner already has PA cover of at least 15 lacs"
          control={
            <Checkbox
              checked={selected === "check1"}
              onChange={(e) => handleSelect("check1", e.target.checked)}
            />
          }
        />

        <Typography variant="body2">OR</Typography>

        <FormControlLabel
          label="The owner does not have driving license"
          control={
            <Checkbox
              checked={selected === "check2"}
              onChange={(e) => handleSelect("check2", e.target.checked)}
            />
          }
        />

        {canProceed && (
          <StyledButton
            variant="contained"
            onClick={handleSubmit}
          >
            Proceed
          </StyledButton>
        )}
      </Stack>
    </Modal>
  );
};

export default PADisclaimer;
